/**
 * Credits context — manages credit purchase modal state app-wide.
 *
 * Provides openPurchaseModal() so any page or component can trigger
 * the credit purchase flow, including on 402 API responses.
 *
 * Provider is added at the App level (App.tsx). The CreditPurchaseModal
 * is rendered once in AppShell, controlled by this context.
 *
 * @spec SPEC:ENGINEERING:FRONTEND:CREDITS
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

interface CreditsContextValue {
  /** Open the purchase modal. Pass true when triggered by an out-of-credits (402) error. */
  openPurchaseModal: (fromOutOfCredits?: boolean) => void
  isOpen: boolean
  fromOutOfCredits: boolean
  close: () => void
}

const CreditsContext = createContext<CreditsContextValue | null>(null)

export function CreditsProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [fromOutOfCredits, setFromOutOfCredits] = useState(false)

  const openPurchaseModal = useCallback((outOfCredits = false) => {
    setFromOutOfCredits(outOfCredits)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const value = useMemo<CreditsContextValue>(
    () => ({ openPurchaseModal, isOpen, fromOutOfCredits, close }),
    [openPurchaseModal, isOpen, fromOutOfCredits, close],
  )

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>
}

export function useCreditsContext(): CreditsContextValue {
  const ctx = useContext(CreditsContext)
  if (!ctx) throw new Error('useCreditsContext must be used within CreditsProvider')
  return ctx
}
